import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Heading,
  Loader,
  Flex,
  Text,
  TextField,
  IconButton,
  Divider,
} from '@vibe/core';
import { Add, Delete, Edit, Check, Close } from '@vibe/icons';
import {
  getLeaveTypes,
  createLeaveType,
  updateLeaveType,
  deleteLeaveType,
  type LeaveType,
} from '../../api/leave.api';

// ─── Component ────────────────────────────────────────────────────────────────

const LeaveTypeManager: React.FC = () => {
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit/Create state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const fetchLeaveTypes = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getLeaveTypes();
      setLeaveTypes(data);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load leave types';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeaveTypes();
  }, [fetchLeaveTypes]);

  const startEdit = useCallback((lt: LeaveType) => {
    setEditingId(lt.id);
    setIsCreating(false);
    setFormName(lt.name);
    setFormDescription(lt.description ?? '');
  }, []);

  const startCreate = useCallback(() => {
    setEditingId(null);
    setIsCreating(true);
    setFormName('');
    setFormDescription('');
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setIsCreating(false);
    setError(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (!formName.trim()) {
      setError('Name is required.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const payload = {
        name: formName.trim(),
        description: formDescription.trim() || undefined,
      };

      if (editingId) {
        await updateLeaveType(editingId, payload);
      } else {
        await createLeaveType(payload);
      }

      setEditingId(null);
      setIsCreating(false);
      await fetchLeaveTypes();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to save leave type';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  }, [
    formName,
    formDescription,
    editingId,
    fetchLeaveTypes,
  ]);

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await deleteLeaveType(id);
        setLeaveTypes((prev) => prev.filter((lt) => lt.id !== id));
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to delete leave type';
        setError(message);
      }
    },
    [],
  );

  const renderForm = () => (
    <Flex gap={Flex.gaps.SMALL} align={Flex.align.END}>
      <div className="rp-leave-type-manager__field">
        <Text type={Text.types.TEXT2}>Name</Text>
        <TextField
          size={TextField.sizes.SMALL}
          value={formName}
          onChange={(value: string) => setFormName(value)}
          placeholder="e.g. Annual Leave"
        />
      </div>
      <div className="rp-leave-type-manager__field">
        <Text type={Text.types.TEXT2}>Description</Text>
        <TextField
          size={TextField.sizes.SMALL}
          value={formDescription}
          onChange={(value: string) => setFormDescription(value)}
          placeholder="Optional description"
        />
      </div>
      <IconButton
        icon={Check}
        size={IconButton.sizes.SMALL}
        onClick={handleSave}
        ariaLabel="Save"
        disabled={isSaving}
      />
      <IconButton
        icon={Close}
        size={IconButton.sizes.SMALL}
        onClick={cancelEdit}
        ariaLabel="Cancel"
      />
    </Flex>
  );

  if (isLoading) {
    return (
      <div className="rp-leave-type-manager">
        <div className="rp-leave-type-manager__loading">
          <Loader size={Loader.sizes.MEDIUM} />
        </div>
      </div>
    );
  }

  return (
    <div className="rp-leave-type-manager">
      <Flex
        justify={Flex.justify.SPACE_BETWEEN}
        align={Flex.align.CENTER}
        className="rp-leave-type-manager__header"
      >
        <Heading type={Heading.types.H3}>Leave Types</Heading>
        <Button
          size={Button.sizes.SMALL}
          leftIcon={Add}
          onClick={startCreate}
          disabled={isCreating}
        >
          Add Type
        </Button>
      </Flex>

      {error && (
        <Text style={{ color: '#e2445c', marginBottom: '8px' }}>{error}</Text>
      )}

      {/* Create form */}
      {isCreating && (
        <>
          <div className="rp-leave-type-manager__form">
            {renderForm()}
          </div>
          <Divider />
        </>
      )}

      {/* Leave type list */}
      {leaveTypes.length === 0 && !isCreating && (
        <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
          No leave types configured. Add one to get started.
        </Text>
      )}

      <div className="rp-leave-type-manager__list">
        {leaveTypes.map((lt) => (
          <div key={lt.id} className="rp-leave-type-manager__item">
            {editingId === lt.id ? (
              /* Inline edit form */
              renderForm()
            ) : (
              /* Display row */
              <Flex
                justify={Flex.justify.SPACE_BETWEEN}
                align={Flex.align.CENTER}
              >
                <Flex gap={Flex.gaps.SMALL} align={Flex.align.CENTER}>
                  <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
                    {lt.name}
                  </Text>
                  {lt.description && (
                    <Text type={Text.types.TEXT2} style={{ color: '#9699a6' }}>
                      — {lt.description}
                    </Text>
                  )}
                </Flex>
                <Flex gap={Flex.gaps.SMALL}>
                  <IconButton
                    icon={Edit}
                    size={IconButton.sizes.SMALL}
                    onClick={() => startEdit(lt)}
                    ariaLabel={`Edit ${lt.name}`}
                  />
                  <IconButton
                    icon={Delete}
                    size={IconButton.sizes.SMALL}
                    onClick={() => handleDelete(lt.id)}
                    ariaLabel={`Delete ${lt.name}`}
                  />
                </Flex>
              </Flex>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LeaveTypeManager;
